import { faChevronDown, faDownload, faPlay, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Combobox,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Pill,
  Popover,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Tabs,
  TagsInput,
  Text,
  Textarea,
  Title,
  UnstyledButton,
  useCombobox,
} from "@mantine/core";
import type { DatesRangeValue } from "@mantine/dates";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useDebouncedValue } from "@mantine/hooks";
import { DateTime } from "luxon";
import React from "react";
import AppointmentReportingApi from "../../data/api/appointmentReportingApi";
import PersonApi from "../../data/api/personsApi";
import ShiftApi from "../../data/api/shiftsApi";
import { useTenures } from "../../hooks/useTenures";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import { BasketballGrade } from "../../types/common/basketballGrades";
import { RefereeCoachGrade } from "../../types/common/refereeCoachGrades";
import { ScorerGrade } from "../../types/common/scorerGrade";
import { StatisticianGrade } from "../../types/common/statisticianGrade";
import { Role } from "../../types/persons/Role";
import { IPerson } from "../../types/persons/person";
import { AppointmentFilter, AppointmentQueryNode } from "../../types/reports/appointmentQuery";
import { AppointmentReportResponse } from "../../types/reports/appointmentReport";
import { AppointmentQueryParserError, parseAppointmentQuery } from "../../util/appointmentQueryParser";

interface AppointmentReportingPanelProps {
  associationId?: string;
  currentPerson?: IPerson | null;
}

interface ShiftOption {
  value: string;
  label: string;
}

interface PersonOption {
  value: string;
  label: string;
}

type BasicTabKey = "basic" | "advanced";

interface BasicFilters {
  dateRange: [Date | null, Date | null];
  stillAppointed: boolean | null;
  appointmentTypes: string[];
  statuses: string[];
  role: string | null;
  roleGrades: string[];
  tenure: string[];
  dayOfWeek: string[];
  persons: string[];
  shifts: string[];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_QUERY = `DateRange("${CURRENT_YEAR}-01-01","${CURRENT_YEAR}-12-31") AND StillAppointed(TRUE)`;

interface QueryFunctionDefinition {
  label: string;
  snippet?: string;
  description: string;
  onSelect?: () => void;
  requiresAssociation?: boolean;
}

interface QueryFunctionGroup {
  id: string;
  title: string;
  description?: string;
  presets?: QueryFunctionDefinition[];
  items: QueryFunctionDefinition[];
}

const ROLE_OPTIONS = [
  { value: "referee", label: "Referee" },
  { value: "scoretable", label: "Scoretable" },
  { value: "supervisor", label: "Supervisor" },
  { value: "refereeCoach", label: "Referee Coach" },
  { value: "administrative", label: "Administrative" },
  { value: "statistician", label: "Statistician" },
];



const APPOINTMENT_TYPE_OPTIONS = [
  { value: "game", label: "Game" },
  { value: "supervisor", label: "Supervisor" },
  { value: "administrator", label: "Administrator" },
];

const APPOINTMENT_STATUS_SEQUENCE: AppointmentStatus[] = [
  AppointmentStatus.rostered,
  AppointmentStatus.published,
  AppointmentStatus.confirmed,
  AppointmentStatus.completed,
  AppointmentStatus.paymentProcessing,
  AppointmentStatus.paymentProcessed,
  AppointmentStatus.paymentProcessedInPerson,
  AppointmentStatus.removed,
  AppointmentStatus.rejected,
  AppointmentStatus.rejectedAcknowledged,
  AppointmentStatus.notCompleted,
  AppointmentStatus.notCompletedAcknowledged,
];

const APPOINTMENT_STATUS_LABEL_MAP: Record<string, string> = {};

const APPOINTMENT_STATUS_OPTIONS = APPOINTMENT_STATUS_SEQUENCE.map((status) => {
  const key = AppointmentStatus[status] as string;
  const label = getAppointmentStatusDisplayLabel(status);
  if (key) {
    APPOINTMENT_STATUS_LABEL_MAP[key] = label;
    APPOINTMENT_STATUS_LABEL_MAP[key.toLowerCase()] = label;
  }
  APPOINTMENT_STATUS_LABEL_MAP[String(status)] = label;
  return { value: key, label };
});

const APPOINTMENT_TYPE_LABEL_MAP: Record<string, string> = {
  game: "Game",
  supervisor: "Supervisor",
  administrator: "Administrator",
};

const DAY_OF_WEEK_OPTIONS = DAY_NAMES.map((label, index) => ({
  value: (index + 1).toString(),
  label,
}));

const SCORER_GRADE_VALUES = [
  ScorerGrade.level0,
  ScorerGrade.level1,
  ScorerGrade.level2,
  ScorerGrade.level3,
  ScorerGrade.level4,
];

const INITIAL_STATE: AppointmentReportResponse = {
  total: 0,
  count: 0,
  results: [],
  timezone: "UTC",
};

function createDefaultBasicFilters(): BasicFilters {
  const start = DateTime.fromObject({ year: CURRENT_YEAR, month: 1, day: 1 }, { zone: "utc" }).toJSDate();
  const end = DateTime.fromObject({ year: CURRENT_YEAR, month: 12, day: 31 }, { zone: "utc" }).toJSDate();
  return {
    dateRange: [start, end],
    stillAppointed: true,
    appointmentTypes: [],
    statuses: [],
    role: null,
    roleGrades: [],
    tenure: [],
    dayOfWeek: [],
    persons: [],
    shifts: [],
  };
}

const connectorBadge = (label: string, key: string) => (
  <Badge key={key} color="gray" variant="light" size="sm">
    {label}
  </Badge>
);

const AppointmentReportingPanel: React.FC<AppointmentReportingPanelProps> = ({ associationId, currentPerson }) => {
  const [queryText, setQueryText] = React.useState(DEFAULT_QUERY);
  const [debouncedQuery, setDebouncedQuery] = useDebouncedState(DEFAULT_QUERY, 300);
  const [advancedExpression, setAdvancedExpression] = React.useState<AppointmentQueryNode | null>(null);
  const [advancedParseError, setAdvancedParseError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<BasicTabKey>("basic");
  const [basicFilters, setBasicFilters] = React.useState<BasicFilters>(() => createDefaultBasicFilters());
  const [result, setResult] = React.useState<AppointmentReportResponse>(INITIAL_STATE);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [requestError, setRequestError] = React.useState<string | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const selectionRef = React.useRef<{ start: number; end: number } | null>(null);

  const [shiftOptions, setShiftOptions] = React.useState<ShiftOption[]>([]);
  const [shiftModalOpen, setShiftModalOpen] = React.useState(false);
  const [shiftSelections, setShiftSelections] = React.useState<string[]>([]);
  const [shiftsLoading, setShiftsLoading] = React.useState(false);
  const [personOptions, setPersonOptions] = React.useState<PersonOption[]>([]);
  const [personsLoading, setPersonsLoading] = React.useState(false);
  const [functionPaletteOpen, setFunctionPaletteOpen] = React.useState(false);
  const [personCount, setPersonCount] = React.useState<number | null>(null);
  const [personCountLoading, setPersonCountLoading] = React.useState(false);

  const { tenureOptions, getTenureLabel } = useTenures(associationId);

  const canUseAdvanced = Boolean(currentPerson?.admin);

  React.useEffect(() => {
    if (!canUseAdvanced && activeTab === "advanced") {
      setActiveTab("basic");
    }
  }, [canUseAdvanced, activeTab]);

  const basicBuild = React.useMemo(() => buildBasicExpression(basicFilters), [basicFilters]);
  const basicExpression = basicBuild.expression;
  const basicError = basicBuild.error;

  const basicRoleGradeOptions = React.useMemo(() => {
    if (!basicFilters.role) {
      return basicFilters.roleGrades.map((value) => ({ value, label: value }));
    }
    const options = getGradeOptionsForRole(basicFilters.role, basicFilters.roleGrades);
    if (!options) {
      return basicFilters.roleGrades.map((value) => ({ value, label: value }));
    }
    const optionValues = new Set(options.map((opt) => opt.value));
    const extras = basicFilters.roleGrades
      .filter((value) => !optionValues.has(value))
      .map((value) => ({ value, label: value }));
    return [...options, ...extras];
  }, [basicFilters.role, basicFilters.roleGrades]);

  const [openFilter, setOpenFilter] = React.useState<string | null>(null);

  const basicFilterChips = React.useMemo<
    Array<
      | {
        id: string;
        label: string;
        summary: string;
        active: boolean;
        type: 'popover';
        content: React.ReactNode;
      }
      | {
        id: string;
        label: string;
        summary: string;
        active: boolean;
        type: 'multi-select';
        data: { value: string; label: string }[];
        value: string[];
        onChange: (value: string[]) => void;
        emptyLabel?: string;
        dropdownWidth?: number;
        helperText?: string;
      }
    >
  >(() => {
    const [startDate, endDate] = basicFilters.dateRange;
    const fromLabel = formatBasicDateLabel(startDate) ?? "Any start";
    const toLabel = formatBasicDateLabel(endDate) ?? "Any end";
    const dateSummary = `${fromLabel} → ${toLabel}`;

    const statusSummary =
      basicFilters.statuses.length > 0
        ? `${basicFilters.statuses.length} selected`
        : "All statuses";

    const typeSummary =
      basicFilters.appointmentTypes.length > 0
        ? `${basicFilters.appointmentTypes.length} selected`
        : "All types";

    const roleSummary = basicFilters.role
      ? `${getRoleDisplayLabel(basicFilters.role)}${basicFilters.roleGrades.length > 0 ? ` • ${basicFilters.roleGrades.length} grade(s)` : ""
      }`
      : "All roles";

    const daySummary =
      basicFilters.dayOfWeek.length > 0 ? `${basicFilters.dayOfWeek.length} selected` : "Any day";

    const tenureSummary =
      basicFilters.tenure.length > 0 ? `${basicFilters.tenure.length} selected` : "All tenures";

    const personSummary = (() => {
      if (basicFilters.persons.length === 0) return "All persons";
      const labels = basicFilters.persons.map((id) => personOptions.find((opt) => opt.value === id)?.label || id);
      if (labels.length <= 2) {
        return labels.join(", ");
      }
      const [first, second, ...rest] = labels;
      return `${first}, ${second}${rest.length > 0 ? ` +${rest.length} more` : ""}`;
    })();

    const shiftSummary =
      basicFilters.shifts.length > 0 ? `${basicFilters.shifts.length} selected` : "All shifts";

    const gradeSummary =
      basicFilters.roleGrades.length > 0 ? `${basicFilters.roleGrades.length} selected` : "All grades";

    const stillAppointedSummary =
      basicFilters.stillAppointed === null
        ? "Either"
        : basicFilters.stillAppointed
          ? "Yes"
          : "No";

    const handleDatePreset = (preset: "thisYear" | "thisMonth" | "lastMonth" | "lastYear") => {
      const now = DateTime.now().setZone("Australia/Melbourne");
      let start: DateTime;
      let end: DateTime;

      switch (preset) {
        case "thisMonth":
          start = now.startOf("month");
          end = now.endOf("month");
          break;
        case "lastMonth": {
          const lastMonth = now.minus({ months: 1 });
          start = lastMonth.startOf("month");
          end = lastMonth.endOf("month");
          break;
        }
        case "lastYear": {
          const lastYear = now.startOf("year").minus({ years: 1 })
          start = lastYear
          end = lastYear.endOf("year")
          break
        }
        case "thisYear":
        default:
          start = now.startOf("year");
          end = now.endOf("year");
          break;
      }

      setBasicFilters((prev) => ({
        ...prev,
        dateRange: [start.toJSDate(), end.toJSDate()],
      }));
    };

    return [
      {
        id: "date",
        label: "Date",
        summary: dateSummary,
        active: true,
        type: "popover" as const,
        content: (
          <Stack gap="sm" miw={280}>
            <Group gap="xs" wrap="wrap">
              <Button size="xs" variant="light" onClick={() => handleDatePreset("thisYear")}>
                This Year
              </Button>
              <Button size="xs" variant="light" onClick={() => handleDatePreset("thisMonth")}>
                This Month
              </Button>
              <Button size="xs" variant="light" onClick={() => handleDatePreset("lastMonth")}>
                Last Month
              </Button>
              <Button size="xs" variant="light" onClick={() => handleDatePreset("lastYear")}>
                Last Year
              </Button>
            </Group>
            <DatePickerInput
              type="range"
              label="Date range"
              value={basicFilters.dateRange}
              onChange={(value) =>
                setBasicFilters((prev) => ({
                  ...prev,
                  dateRange: value ?? [null, null],
                }))
              }
              allowSingleDateInRange
              clearable
              popoverProps={{ withinPortal: false, keepMounted: true, trapFocus: true }}
            />
            <Group justify="flex-end">
              <Button size="xs" onClick={() => setOpenFilter(null)}>
                Done
              </Button>
            </Group>
          </Stack>
        ),
      },
      {
        id: "still",
        label: "Still appointed",
        summary: stillAppointedSummary,
        active: basicFilters.stillAppointed !== true,
        type: "popover" as const,
        content: (
          <Stack gap="sm" style={{ width: 220 }}>
            <SegmentedControl
              data={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
                { label: "Either", value: "either" },
              ]}
              fullWidth
              value={
                basicFilters.stillAppointed === null
                  ? "either"
                  : basicFilters.stillAppointed
                    ? "true"
                    : "false"
              }
              onChange={(value) => {
                setBasicFilters((prev) => ({
                  ...prev,
                  stillAppointed: value === "either" ? null : value === "true",
                }));
                setOpenFilter(null);
              }}
            />
            <Text size="xs" c="dimmed" ta="left" style={{ lineHeight: 1.4 }}>
              Selecting No or Either includes officials who were removed from games. The removed statuses can offer
              more precise filtering.
            </Text>
          </Stack>
        ),
      },
      {
        id: "statuses",
        label: "Status",
        summary: statusSummary,
        active: basicFilters.statuses.length > 0,
        type: "multi-select" as const,
        data: APPOINTMENT_STATUS_OPTIONS,
        value: basicFilters.statuses,
        onChange: (value: string[]) =>
          setBasicFilters((prev) => ({
            ...prev,
            statuses: value,
          })),
        emptyLabel: "No status matches",
        dropdownWidth: 320,
        helperText: "Statuses such as Removed or Rejected typically only appear when Still appointed is set to No or Either.",
      },
      {
        id: "types",
        label: "Type",
        summary: typeSummary,
        active: basicFilters.appointmentTypes.length > 0,
        type: "multi-select" as const,
        data: APPOINTMENT_TYPE_OPTIONS,
        value: basicFilters.appointmentTypes,
        onChange: (value: string[]) =>
          setBasicFilters((prev) => ({
            ...prev,
            appointmentTypes: value,
          })),
        emptyLabel: "No type matches",
      },
      {
        id: "role",
        label: "Role",
        summary: roleSummary,
        active: Boolean(basicFilters.role),
        type: "popover" as const,
        content: (
          <Stack gap="sm" miw={280}>
            <Select
              label="Role"
              data={ROLE_OPTIONS}
              value={basicFilters.role}
              onChange={(value) => {
                setBasicFilters((prev) => ({
                  ...prev,
                  role: value,
                  roleGrades: value && value === prev.role ? prev.roleGrades : [],
                }));
                setOpenFilter("role");
              }}
              searchable
              clearable
            />
            {basicFilters.role && (
              <MultiSelectChip
                label="Grades"
                summary={gradeSummary}
                data={basicRoleGradeOptions}
                value={basicFilters.roleGrades}
                onChange={(value: string[]) =>
                  setBasicFilters((prev) => ({
                    ...prev,
                    roleGrades: value,
                  }))
                }
                withinPortal={false}
                emptyLabel="No grade matches"
              />
            )}
            <Group justify="flex-end">
              <Button size="xs" onClick={() => setOpenFilter(null)}>
                Done
              </Button>
            </Group>
          </Stack>
        ),
      },
      {
        id: "days",
        label: "Days",
        summary: daySummary,
        active: basicFilters.dayOfWeek.length > 0,
        type: "multi-select" as const,
        data: DAY_OF_WEEK_OPTIONS,
        value: basicFilters.dayOfWeek,
        onChange: (value: string[]) =>
          setBasicFilters((prev) => ({
            ...prev,
            dayOfWeek: value,
          })),
      },
      {
        id: "tenure",
        label: "Tenure",
        summary: tenureSummary,
        active: basicFilters.tenure.length > 0,
        type: "multi-select" as const,
        data: tenureOptions,
        value: basicFilters.tenure,
        onChange: (value: string[]) =>
          setBasicFilters((prev) => ({
            ...prev,
            tenure: value,
          })),
      },
      {
        id: "persons",
        label: "Persons",
        summary: personSummary,
        active: basicFilters.persons.length > 0,
        type: "multi-select" as const,
        data: (() => {
          const known = new Set(personOptions.map((opt) => opt.value));
          const extras = basicFilters.persons
            .filter((id) => !known.has(id))
            .map((id) => ({ value: id, label: id }));
          return [...personOptions, ...extras];
        })(),
        value: basicFilters.persons,
        onChange: (value: string[]) =>
          setBasicFilters((prev) => ({
            ...prev,
            persons: value,
          })),
        emptyLabel: personsLoading
          ? "Loading persons..."
          : personOptions.length === 0
            ? "No persons available"
            : "No matches",
        helperText: "Filtered to active members of this association.",
        dropdownWidth: 320,
      },
      {
        id: "shifts",
        label: "Shifts",
        summary: shiftSummary,
        active: basicFilters.shifts.length > 0,
        type: "multi-select" as const,
        data: (() => {
          const known = new Set(shiftOptions.map((opt) => opt.value));
          const extras = basicFilters.shifts
            .filter((id) => !known.has(id))
            .map((id) => ({ value: id, label: id }));
          return [...shiftOptions, ...extras];
        })(),
        value: basicFilters.shifts,
        onChange: (value: string[]) =>
          setBasicFilters((prev) => ({
            ...prev,
            shifts: value,
          })),
        emptyLabel: shiftOptions.length === 0 ? "No shifts available" : "No matches",
      },
    ];
  }, [basicFilters, basicRoleGradeOptions, personOptions, personsLoading, shiftOptions, setBasicFilters, setOpenFilter]);

  const activeExpression = activeTab === "advanced" ? advancedExpression : basicExpression;
  const activeError = activeTab === "advanced" ? advancedParseError : basicError;

  // Debounce the active expression to avoid excessive API calls
  const [debouncedExpression] = useDebouncedValue(activeExpression, 500);

  // Track if expression has changed but not yet debounced
  const isExpressionPending = activeExpression !== debouncedExpression;

  React.useEffect(() => {
    if (activeTab !== "advanced") {
      setFunctionPaletteOpen(false);
    }
  }, [activeTab]);

  const updateSelectionCache = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    selectionRef.current = {
      start: textarea.selectionStart ?? textarea.value.length,
      end: textarea.selectionEnd ?? textarea.value.length,
    };
  }, []);

  React.useEffect(() => {
    setDebouncedQuery(queryText);
  }, [queryText, setDebouncedQuery]);

  React.useEffect(() => {
    updateSelectionCache();
  }, [updateSelectionCache]);

  React.useEffect(() => {
    if (!debouncedQuery || debouncedQuery.trim().length === 0) {
      setAdvancedExpression(null);
      setAdvancedParseError("Enter a query to get started.");
      return;
    }

    try {
      const parsed = parseAppointmentQuery(debouncedQuery);
      if (!expressionContainsDateRange(parsed)) {
        throw new AppointmentQueryParserError("Queries must include at least one DateRange(...) filter", 0);
      }
      setAdvancedExpression(parsed);
      setAdvancedParseError(null);
    } catch (err) {
      if (err instanceof AppointmentQueryParserError) {
        setAdvancedParseError(`${err.message} at position ${err.position + 1}`);
      } else {
        setAdvancedParseError((err as Error).message);
      }
      setAdvancedExpression(null);
    }
  }, [debouncedQuery]);

  React.useEffect(() => {
    let cancelled = false;
    if (!associationId) {
      setPersonOptions([]);
      setPersonsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setPersonsLoading(true);
    PersonApi.getActiveAtAssociation(associationId)
      .then((persons) => {
        if (cancelled) return;
        const filtered = (persons || []).filter((person) =>
          person.associations?.some?.((assoc) => assoc.associationId === associationId),
        );
        const options = filtered.map((person) => {
          const baseName = person.fullName
          const label = [baseName, person.externalIdentifier].filter(Boolean).join(" · ");
          return { value: person._id, label: label || person._id } as PersonOption;
        });
        setPersonOptions(options);
      })
      .catch((err) => {
        console.error("Failed to load persons for association", err);
      })
      .finally(() => {
        if (!cancelled) setPersonsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [associationId]);

  React.useEffect(() => {
    let cancelled = false;
    if (!associationId) {
      setShiftOptions([]);
      setShiftsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setShiftsLoading(true);
    ShiftApi.getManageableShifts()
      .then((resp) => {
        if (cancelled) return;
        const shifts = resp?.shifts || resp?.data?.shifts || [];
        const filtered = shifts.filter((shift) => shift.associationId === associationId);
        const options = filtered.map((shift) => ({
          value: shift._id,
          label: shift.name,
        }));
        setShiftOptions(options);
      })
      .catch((err) => {
        console.error("Failed to load shifts", err);
      })
      .finally(() => {
        if (!cancelled) setShiftsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [associationId]);

  // Fetch person count when debounced expression changes
  React.useEffect(() => {
    let cancelled = false;

    if (!associationId) {
      setPersonCount(null);
      return () => {
        cancelled = true;
      };
    }

    // Only fetch count if we have a valid expression
    if (!debouncedExpression) {
      setPersonCount(null);
      setPersonCountLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // Extract only person-related filters to count persons in the database
    const personFilters = extractPersonFilters(debouncedExpression);

    // If no person filters are specified, still count all people in the association
    setPersonCountLoading(true);
    AppointmentReportingApi.countPersonsWithFilters({
      associationId,
      ...personFilters,
    })
      .then((count) => {
        if (cancelled) return;
        setPersonCount(count);
      })
      .catch((err) => {
        console.error("Failed to count persons", err);
        if (!cancelled) setPersonCount(null);
      })
      .finally(() => {
        if (!cancelled) setPersonCountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [associationId, debouncedExpression]);

  const commitExpression = React.useCallback(
    (nextExpression: AppointmentQueryNode) => {
      const nextText = stringifyAppointmentQuery(nextExpression);
      setAdvancedExpression(nextExpression);
      setAdvancedParseError(null);
      setQueryText(nextText);
      setDebouncedQuery(nextText);
    },
    [setDebouncedQuery],
  );

  const insertSnippet = React.useCallback((snippet: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setQueryText((prev) => {
        const base = prev || "";
        const needsConnector = base.trimEnd().length > 0 && !base.trimEnd().endsWith("(");
        const connector = needsConnector ? " AND " : "";
        const updatedText = `${base}${connector}${snippet}`;
        selectionRef.current = { start: updatedText.length, end: updatedText.length };
        return updatedText;
      });
      return;
    }

    textarea.focus();
    if (selectionRef.current) {
      const { start, end } = selectionRef.current;
      textarea.setSelectionRange(start, end);
    }

    const { selectionStart, selectionEnd } = textarea;
    const base = queryText || "";
    const before = base.slice(0, selectionStart);
    const after = base.slice(selectionEnd);
    const needsConnector = before.trimEnd().length > 0 && !before.trimEnd().endsWith("(");
    const connector = needsConnector ? " AND " : "";
    const updated = `${before}${connector}${snippet}${after}`;
    setQueryText(updated);
    const caret = selectionStart + connector.length + snippet.length;
    selectionRef.current = { start: caret, end: caret };
    requestAnimationFrame(() => {
      textarea.setSelectionRange(caret, caret);
      updateSelectionCache();
    });
  }, [queryText, updateSelectionCache]);

  const handleFilterChange = React.useCallback(
    (path: number[], updatedFilter: AppointmentFilter) => {
      if (!advancedExpression) return;
      const nextExpression = updateExpressionNode(advancedExpression, path, (node) => ({
        ...node,
        filter: updatedFilter,
      }));
      commitExpression(nextExpression);
    },
    [advancedExpression, commitExpression],
  );

  const handleFilterRemove = React.useCallback(
    (path: number[]) => {
      if (!advancedExpression) return;
      const nextExpression = removeExpressionNode(advancedExpression, path);
      if (!nextExpression) {
        setAdvancedExpression(null);
        setQueryText("");
        setAdvancedParseError("Enter a query to get started.");
        setDebouncedQuery("");
        return;
      }
      commitExpression(nextExpression);
    },
    [advancedExpression, commitExpression, setDebouncedQuery],
  );

  const handleRunQuery = async () => {
    if (!associationId || !activeExpression) {
      setRequestError(!associationId ? "Select an association first." : "Invalid query.");
      return;
    }

    setLoading(true);
    setRequestError(null);
    try {
      const payload = {
        associationId,
        expression: activeExpression,
        limit: 100,
      };
      const data = await AppointmentReportingApi.query(payload);
      setResult(data);
    } catch (err) {
      console.error("Failed to query appointments report", err);
      setRequestError(
        err instanceof Error ? err.message : "Unable to run the query. Please try again or adjust your filters.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!associationId || !activeExpression) return;
    setExporting(true);
    try {
      const blob = await AppointmentReportingApi.exportCsv({
        associationId,
        expression: activeExpression,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `appointments-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export appointments report", err);
      setRequestError(err instanceof Error ? err.message : "Unable to export report.");
    } finally {
      setExporting(false);
    }
  };

  const openShiftModal = () => {
    setShiftSelections([]);
    setShiftModalOpen(true);
  };

  const applyShiftFilter = () => {
    if (shiftSelections.length === 0) {
      setShiftModalOpen(false);
      return;
    }
    const snippet = `Shift(${shiftSelections.map((id) => `"${id}"`).join(",")})`;
    insertSnippet(snippet);
    setShiftModalOpen(false);
  };

  const insertDateRangePreset = React.useCallback(
    (daysBack: number) => {
      const end = DateTime.now().setZone("utc").startOf("day");
      const start = end.minus({ days: daysBack });
      insertSnippet(`DateRange("${start.toISODate()}","${end.toISODate()}")`);
    },
    [insertSnippet],
  );

  const insertMonthPreset = React.useCallback(
    (offset: number) => {
      const base = DateTime.now().setZone("utc").startOf("day").plus({ months: offset });
      const start = base.startOf("month");
      const end = base.endOf("month").startOf("day");
      insertSnippet(`DateRange("${start.toISODate()}","${end.toISODate()}")`);
    },
    [insertSnippet],
  );

  const functionGroups = React.useMemo<QueryFunctionGroup[]>(
    () => [
      {
        id: "date-range",
        title: "Date Range",
        description: "Limit appointments to specific dates or periods.",
        presets: [
          {
            label: "This month",
            onSelect: () => insertMonthPreset(0),
            description: "Appointments between the start and end of the current month.",
          },
          {
            label: "Last month",
            onSelect: () => insertMonthPreset(-1),
            description: "Appointments from the previous calendar month.",
          },
          {
            label: "Last 7 days",
            onSelect: () => insertDateRangePreset(6),
            description: "Appointments from the past week (including today).",
          },
        ],
        items: [
          {
            label: 'DateRange("from", "to")',
            snippet: 'DateRange("2024-01-01","2024-01-31")',
            description: "Specify exact start and end ISO dates.",
          },
        ],
      },
      {
        id: "day-of-week",
        title: "Day of Week",
        description: "Match appointments that occur on selected weekdays.",
        items: [
          {
            label: 'Day("Monday", "Tuesday", ...)',
            snippet: 'Day("Monday","Tuesday","Wednesday","Thursday","Friday")',
            description: "Provide one or more day names (Monday–Sunday).",
          },
        ],
      },
      {
        id: "role",
        title: "Roles & Grades",
        description: "Filter officials by role; add one or more grades as extra arguments.",
        items: [
          {
            label: 'RoleFilter("role", grade1, grade2, ...)',
            snippet: 'RoleFilter("referee",3,4)',
            description: "Role can be referee, scoretable, supervisor, etc. Add grades after the role to target specific levels.",
          },
        ],
      },
      {
        id: "appointment-type",
        title: "Appointment Type",
        description: "Limit to game, supervisor, or administrator appointments.",
        items: [
          {
            label: 'AppointmentType("game", "supervisor", ...)',
            snippet: 'AppointmentType("game","supervisor")',
            description: "Provide one or more appointment types.",
          },
        ],
      },
      {
        id: "status",
        title: "Status",
        description: "Filter by appointment workflow status or whether the appointment is still active.",
        items: [
          {
            label: 'AppointmentStatus("confirmed", "published", ...)',
            snippet: 'AppointmentStatus("confirmed","published")',
            description: "Select one or more statuses such as rostered, confirmed, or completed.",
          },
          {
            label: "StillAppointed(TRUE|FALSE)",
            snippet: "StillAppointed(TRUE)",
            description: "TRUE keeps currently appointed officials; FALSE includes removed or acknowledged ones.",
          },
        ],
      },
      {
        id: "position",
        title: "Positions",
        description: "Match appointment position codes (e.g., Ref1, Ref2).",
        items: [
          {
            label: 'Position("Ref1", "Ref2", ...)',
            snippet: 'Position("Ref1","Ref2")',
            description: "Provide one or more position identifiers.",
          },
        ],
      },
      {
        id: "person",
        title: "Person",
        description: "Filter to specific people within the active association.",
        presets: [
          {
            label: "Select persons…",
            onSelect: () => {
              if (personOptions.length === 0) return;
              const snippet = `Person(${personOptions
                .slice(0, 3)
                .map((opt) => `"${opt.value}"`)
                .join(",")})`;
              insertSnippet(snippet);
            },
            requiresAssociation: true,
          },
        ],
        items: [
          {
            label: 'Person("PERSON_ID", …)',
            snippet: 'Person("PERSON_ID")',
            description: "Provide one or more person IDs. Use the basic tab for a searchable selector.",
          },
        ],
      },
      {
        id: "tenure",
        title: "Tenure",
        description: "Include people with selected association tenure values.",
        items: [
          {
            label: 'Tenure("permanent", ...)',
            snippet: 'Tenure("permanent")',
            description: "Filter by person tenure types (configured per association)",
          },
        ],
      },
      {
        id: "shift",
        title: "Shifts",
        description: "Filter by specific shift IDs or use the selector.",
        presets: [
          {
            label: "Select shifts…",
            onSelect: () => openShiftModal(),
            requiresAssociation: true,
          },
        ],
        items: [
          {
            label: 'Shift("SHIFT_ID", …)',
            snippet: 'Shift("SHIFT_ID")',
            description: "Insert shift IDs manually when you already know them.",
          },
        ],
      },
    ],
    [insertDateRangePreset, insertMonthPreset, openShiftModal, personOptions],
  );

  const handleFunctionSelect = React.useCallback(
    (definition: QueryFunctionDefinition, disabled?: boolean) => {
      if (disabled) return;
      updateSelectionCache();
      if (definition.snippet) {
        insertSnippet(definition.snippet);
      }
      if (definition.onSelect) {
        definition.onSelect();
      }
      setFunctionPaletteOpen(false);
    },
    [insertSnippet, updateSelectionCache],
  );

  const pillElements = React.useMemo(() => {
    if (!advancedExpression) return null;
    return buildExpressionElements({
      node: advancedExpression,
      path: [],
      parentType: null,
      onFilterChange: handleFilterChange,
      shiftOptions,
      personOptions,
      tenureOptions,
      onRemove: handleFilterRemove,
    });
  }, [advancedExpression, handleFilterChange, handleFilterRemove, shiftOptions, personOptions, tenureOptions]);

  return (
    <>
      <Card shadow="sm" radius="md" mb="lg" withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={4}>Appointments Reporting</Title>
              <Text size="sm" c="dimmed">
                Choose between a simple filter experience or craft custom logic. All filters are combined with AND logic.
              </Text>
            </div>
          </Group>

          <Tabs
            value={activeTab}
            onChange={(value) => {
              const nextTab = (value as BasicTabKey) || "basic";
              if (nextTab === "advanced" && !canUseAdvanced) {
                setActiveTab("basic");
                return;
              }
              if (nextTab === "advanced" && activeTab !== "advanced" && basicExpression) {
                const text = stringifyAppointmentQuery(basicExpression);
                setAdvancedExpression(basicExpression);
                setAdvancedParseError(null);
                setQueryText(text);
                setDebouncedQuery(text);
                selectionRef.current = { start: text.length, end: text.length };
              }
              setActiveTab(nextTab);
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="basic">Basic filters</Tabs.Tab>
              {canUseAdvanced && <Tabs.Tab value="advanced">Advanced query</Tabs.Tab>}
            </Tabs.List>

            <Tabs.Panel value="basic">
              <Stack gap="lg" pt="md">
                {/* Person Filters Section */}
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600} c="dimmed">
                      Person Filters
                    </Text>
                    {personCountLoading || isExpressionPending ? (
                      <Group gap="xs">
                        <Loader size="xs" />
                        <Text size="xs" c="dimmed">
                          Counting...
                        </Text>
                      </Group>
                    ) : personCount !== null ? (
                      <Badge size="lg" variant="light" color="blue">
                        {personCount} {personCount === 1 ? "person" : "people"}
                      </Badge>
                    ) : null}
                  </Group>
                  <Group gap="sm" wrap="wrap">
                    {basicFilterChips
                      .filter((chip) => ["role", "tenure", "persons"].includes(chip.id))
                      .map((chip) => {
                        if (chip.type === "popover") {
                          const opened = openFilter === chip.id;
                          return (
                            <Popover
                              key={chip.id}
                              opened={opened}
                              onChange={(state) => setOpenFilter(state ? chip.id : null)}
                              withinPortal
                              trapFocus
                            >
                              <Popover.Target>
                                <Button
                                  variant={chip.active ? "filled" : "light"}
                                  radius="sm"
                                  size="sm"
                                  onClick={() => setOpenFilter((current) => (current === chip.id ? null : chip.id))}
                                  rightSection={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                                  styles={(theme) => ({
                                    root: {
                                      height: "auto",
                                      padding: `${theme.spacing.xs * 0.75}px ${theme.spacing.sm}px`,
                                      borderRadius: theme.radius.sm,
                                    },
                                    inner: { alignItems: "center", textAlign: "left", gap: theme.spacing.xs },
                                    section: { marginTop: 0 },
                                  })}
                                >
                                  <Stack gap={2} style={{ textAlign: "left" }}>
                                    <Text size="xs" fw={600}>
                                      {chip.label}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {chip.summary}
                                    </Text>
                                  </Stack>
                                </Button>
                              </Popover.Target>
                              <Popover.Dropdown>{chip.content}</Popover.Dropdown>
                            </Popover>
                          );
                        }

                        return (
                          <MultiSelectChip
                            key={chip.id}
                            label={chip.label}
                            summary={chip.summary}
                            value={chip.value}
                            data={chip.data}
                            onChange={chip.onChange}
                            emptyLabel={chip.emptyLabel}
                            dropdownWidth={chip.dropdownWidth}
                            helperText={chip.helperText}
                          />
                        );
                      })}
                  </Group>
                </div>

                <Divider />

                {/* Appointment Filters Section */}
                <div>
                  <Text size="sm" fw={600} mb="xs" c="dimmed">
                    Appointment Filters
                  </Text>
                  <Group gap="sm" wrap="wrap">
                    {basicFilterChips
                      .filter((chip) => !["role", "tenure", "persons"].includes(chip.id))
                      .map((chip) => {
                        if (chip.type === "popover") {
                          const opened = openFilter === chip.id;
                          return (
                            <Popover
                              key={chip.id}
                              opened={opened}
                              onChange={(state) => setOpenFilter(state ? chip.id : null)}
                              withinPortal
                              trapFocus
                            >
                              <Popover.Target>
                                <Button
                                  variant={chip.active ? "filled" : "light"}
                                  radius="sm"
                                  size="sm"
                                  onClick={() => setOpenFilter((current) => (current === chip.id ? null : chip.id))}
                                  rightSection={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                                  styles={(theme) => ({
                                    root: {
                                      height: "auto",
                                      padding: `${theme.spacing.xs * 0.75}px ${theme.spacing.sm}px`,
                                      borderRadius: theme.radius.sm,
                                    },
                                    inner: { alignItems: "center", textAlign: "left", gap: theme.spacing.xs },
                                    section: { marginTop: 0 },
                                  })}
                                >
                                  <Stack gap={2} style={{ textAlign: "left" }}>
                                    <Text size="xs" fw={600}>
                                      {chip.label}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {chip.summary}
                                    </Text>
                                  </Stack>
                                </Button>
                              </Popover.Target>
                              <Popover.Dropdown>{chip.content}</Popover.Dropdown>
                            </Popover>
                          );
                        }

                        return (
                          <MultiSelectChip
                            key={chip.id}
                            label={chip.label}
                            summary={chip.summary}
                            value={chip.value}
                            data={chip.data}
                            onChange={chip.onChange}
                            emptyLabel={chip.emptyLabel}
                            dropdownWidth={chip.dropdownWidth}
                            helperText={chip.helperText}
                          />
                        );
                      })}
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => {
                        setBasicFilters(createDefaultBasicFilters());
                        setOpenFilter(null);
                      }}
                    >
                      Reset
                    </Button>
                  </Group>
                </div>

                {basicError && (
                  <Alert color="red" variant="light">
                    {basicError}
                  </Alert>
                )}
              </Stack>
            </Tabs.Panel>

            {canUseAdvanced && (
              <Tabs.Panel value="advanced">
                <Stack gap="md" pt="md">
                  <Group justify="flex-end">
                    <Popover
                      opened={functionPaletteOpen}
                      onChange={setFunctionPaletteOpen}
                      position="bottom-start"
                      withinPortal
                      shadow="md"
                    >
                      <Popover.Target>
                        <Button
                          variant="light"
                          leftSection={<FontAwesomeIcon icon={faPlus} size="sm" />}
                          onClick={() => {
                            updateSelectionCache();
                            setFunctionPaletteOpen((open) => !open);
                          }}
                        >
                          Add function
                        </Button>
                      </Popover.Target>
                      <Popover.Dropdown style={{ width: 360 }}>
                        <Stack gap="xs">
                          <ScrollArea h={260} type="auto">
                            <Stack gap="sm">
                              {functionGroups.map((group, groupIndex) => (
                                <Stack key={group.id} gap={4}>
                                  <Stack gap={2}>
                                    <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                                      {group.title}
                                    </Text>
                                    {group.description && (
                                      <Text size="xs" c="dimmed">
                                        {group.description}
                                      </Text>
                                    )}
                                  </Stack>

                                  {group.presets && group.presets.length > 0 && (
                                    <Group gap={4} wrap="wrap">
                                      {group.presets.map((preset) => {
                                        const disabled =
                                          preset.requiresAssociation &&
                                          (!associationId || shiftsLoading || shiftOptions.length === 0);
                                        return (
                                          <Button
                                            key={`${group.id}-preset-${preset.label}`}
                                            size="xs"
                                            variant="light"
                                            disabled={disabled}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => handleFunctionSelect(preset, disabled)}
                                          >
                                            {preset.label}
                                          </Button>
                                        );
                                      })}
                                    </Group>
                                  )}

                                  <Stack gap={4}>
                                    {group.items.map((definition) => {
                                      const disabled =
                                        definition.requiresAssociation &&
                                        (!associationId || shiftsLoading || shiftOptions.length === 0);
                                      return (
                                        <UnstyledButton
                                          key={`${group.id}-item-${definition.label}`}
                                          onMouseDown={(event) => event.preventDefault()}
                                          onClick={() => handleFunctionSelect(definition, disabled)}
                                          style={{
                                            padding: "6px 8px",
                                            borderRadius: 8,
                                            transition: "background-color 120ms ease, color 120ms ease",
                                            backgroundColor: disabled
                                              ? "transparent"
                                              : "rgba(255, 255, 255, 0.04)",
                                            border: disabled
                                              ? "1px solid transparent"
                                              : "1px solid rgba(255, 255, 255, 0.06)",
                                            outline: "none",
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            opacity: disabled ? 0.5 : 1,
                                          }}
                                          aria-disabled={disabled}
                                        >
                                          <Stack gap={2} align="flex-start">
                                            <Text size="sm" fw={500}>
                                              {definition.label}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                              {definition.description}
                                            </Text>
                                            {definition.snippet && (
                                              <Text
                                                size="xs"
                                                c="var(--mantine-color-blue-3)"
                                                style={{ fontFamily: "var(--mantine-font-family-monospace)" }}
                                              >
                                                {definition.snippet}
                                              </Text>
                                            )}
                                          </Stack>
                                        </UnstyledButton>
                                      );
                                    })}
                                  </Stack>

                                  {groupIndex < functionGroups.length - 1 && <Divider my="xs" />}
                                </Stack>
                              ))}
                            </Stack>
                          </ScrollArea>
                          <Text size="xs" c="dimmed">
                            Snippets insert at your last cursor position; if none is set, they append to the end.
                          </Text>
                        </Stack>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>

                  <Textarea
                    ref={textareaRef}
                    label="Query"
                    minRows={4}
                    value={queryText}
                    onChange={(event) => setQueryText(event.currentTarget.value)}
                    autosize
                    placeholder='Example: (Day("Monday","Tuesday") OR Day("Wednesday")) AND RoleFilter("referee",3,4)'
                    onFocus={updateSelectionCache}
                    onSelect={updateSelectionCache}
                    onClick={updateSelectionCache}
                    onKeyUp={updateSelectionCache}
                    onMouseUp={updateSelectionCache}
                    styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: 12 } }}
                  />

                  {advancedParseError ? (
                    <Alert color="red" variant="light">
                      {advancedParseError}
                    </Alert>
                  ) : (
                    advancedExpression && (
                      <Card padding="sm" radius="md" withBorder>
                        <Pill.Group>{pillElements}</Pill.Group>
                      </Card>
                    )
                  )}
                </Stack>
              </Tabs.Panel>
            )}
          </Tabs>

          {!associationId && (
            <Alert color="yellow" variant="light">
              Select an association to enable reporting filters and results.
            </Alert>
          )}
          <Group>
            <Button
              onClick={handleRunQuery}
              leftSection={<FontAwesomeIcon icon={faPlay} size="sm" />}
              loading={loading}
              disabled={!associationId || !activeExpression || !!activeError}
            >
              Run query
            </Button>
            <Button
              variant="light"
              leftSection={<FontAwesomeIcon icon={faDownload} size="sm" />}
              onClick={handleExport}
              loading={exporting}
              disabled={!associationId || !activeExpression || !!activeError}
            >
              Download all ({result.total.toLocaleString()} rows)
            </Button>
          </Group>

          {requestError && (
            <Alert color="red" variant="light">
              {requestError}
            </Alert>
          )}

          <ResultsTable loading={loading} data={result} />
        </Stack>
      </Card>

      <Modal
        opened={shiftModalOpen}
        onClose={() => setShiftModalOpen(false)}
        title="Select shifts"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Choose one or more shifts to include in the query. Matching Shift IDs will be inserted into the text input.
          </Text>
          <MultiSelect
            data={shiftOptions}
            value={shiftSelections}
            onChange={setShiftSelections}
            searchable
            nothingFoundMessage={shiftsLoading ? "Loading shifts…" : "No shifts found"}
            disabled={shiftsLoading}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setShiftModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={applyShiftFilter} disabled={shiftSelections.length === 0}>
              Insert
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

interface ResultsTableProps {
  loading: boolean;
  data: AppointmentReportResponse;
}

const ResultsTable: React.FC<ResultsTableProps> = ({ loading, data }) => {
  if (loading) {
    return (
      <Group justify="center">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!data || data.count === 0) {
    return (
      <Alert color="gray" variant="light">
        No appointments found for the current filters.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={500}>
          Showing {data.count.toLocaleString()} of {data.total.toLocaleString()} appointments
        </Text>
        {data.total > data.count && (
          <Badge color="blue" variant="light">
            Download to view all rows
          </Badge>
        )}
      </Group>

      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Day</Table.Th>
            <Table.Th>Start</Table.Th>
            <Table.Th>Person</Table.Th>
            <Table.Th>Tenure</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Grade</Table.Th>
            <Table.Th>Position</Table.Th>
            <Table.Th>Shift</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Status comments</Table.Th>
            <Table.Th>Total</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.results.map((row) => (
            <Table.Tr key={row.appointmentId}>
              {(() => {
                const zone = row.timezone || data.timezone;
                const dt = row.start
                  ? DateTime.fromISO(row.start, { setZone: true }).setZone(zone, { keepLocalTime: false })
                  : null;
                const dayLabel = dt
                  ? dt.toFormat("cccc")
                  : typeof row.dayOfWeek === "number"
                    ? DAY_NAMES[(row.dayOfWeek - 1 + 7) % 7]
                    : "-";
                return (
                  <>
                    <Table.Td>{dt ? dt.toFormat("yyyy-MM-dd") : "-"}</Table.Td>
                    <Table.Td>{dayLabel}</Table.Td>
                    <Table.Td>{dt ? dt.toFormat("HH:mm") : "-"}</Table.Td>
                  </>
                );
              })()}
              <Table.Td>{row.person?.displayName || "-"}</Table.Td>
              <Table.Td>{formatTenure(row.person?.tenure)}</Table.Td>
              <Table.Td>{row.roleLabel || "-"}</Table.Td>
              <Table.Td>{row.typeLabel || row.type || "-"}</Table.Td>
              <Table.Td>{formatGrade(row.role, row.person?.grade)}</Table.Td>
              <Table.Td>{row.position || "-"}</Table.Td>
              <Table.Td>{row.shift?.shiftName || row.shift?.shiftId || "-"}</Table.Td>
              <Table.Td>{row.statusLabel}</Table.Td>
              <Table.Td>{row.statusComments || "-"}</Table.Td>
              <Table.Td>{row.payment?.total != null ? `$${row.payment.total.toFixed(2)}` : "-"}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

type ExpressionPath = number[];

interface ExpressionRenderParams {
  node: AppointmentQueryNode;
  path: ExpressionPath;
  parentType: "and" | "or" | null;
  onFilterChange: (path: ExpressionPath, filter: AppointmentFilter) => void;
  shiftOptions: ShiftOption[];
  personOptions: PersonOption[];
  tenureOptions: { value: string; label: string }[];
  onRemove: (path: ExpressionPath) => void;
}

function buildExpressionElements(params: ExpressionRenderParams): React.ReactNode[] {
  const { node, path, parentType, onFilterChange, shiftOptions, personOptions, tenureOptions } = params;
  const keyBase = path.length > 0 ? path.join("-") : "root";

  if (node.type === "filter") {
    return [
      <FilterPillComponent
        key={`filter-${keyBase}`}
        filter={node.filter}
        path={path}
        onFilterChange={onFilterChange}
        shiftOptions={shiftOptions}
        personOptions={personOptions}
        tenureOptions={tenureOptions}
        onRemove={params.onRemove}
      />,
    ];
  }

  const needsParentheses = parentType !== null && parentType !== node.type;

  if (!needsParentheses) {
    const content: React.ReactNode[] = [];
    node.nodes.forEach((child, index) => {
      if (index > 0) {
        content.push(connectorBadge(node.type.toUpperCase(), `${keyBase}-${node.type}-${index}`));
      }
      content.push(
        ...buildExpressionElements({
          node: child,
          path: [...path, index],
          parentType: node.type,
          onFilterChange,
          shiftOptions,
          personOptions,
          tenureOptions,
          onRemove: params.onRemove,
        }),
      );
    });
    return content;
  }

  const groupedContent = node.nodes.flatMap((child, index) => {
    const inner = buildExpressionElements({
      node: child,
      path: [...path, index],
      parentType: node.type,
      onFilterChange,
      shiftOptions,
      personOptions,
      tenureOptions,
      onRemove: params.onRemove,
    });
    const items: React.ReactNode[] = [];
    if (index > 0) {
      items.push(connectorBadge(node.type.toUpperCase(), `${keyBase}-${node.type}-${index}`));
    }
    items.push(...inner);
    return items;
  });

  return [
    <Box
      key={`group-${keyBase}`}
      style={{
        backgroundColor: "var(--mantine-color-dark-6)",
        borderRadius: "var(--mantine-radius-sm)",
        padding: "6px 10px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
      }}
    >
      <Text span>(</Text>
      {groupedContent.map((element, idx) => (
        <span key={`${keyBase}-wrapped-${idx}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {element}
        </span>
      ))}
      <Text span>)</Text>
    </Box>,
  ];
}

interface FilterPillProps {
  filter: AppointmentFilter;
  path: ExpressionPath;
  onFilterChange: (path: ExpressionPath, filter: AppointmentFilter) => void;
  shiftOptions: ShiftOption[];
  personOptions: PersonOption[];
  tenureOptions: { value: string; label: string }[];
  onRemove: (path: ExpressionPath) => void;
}

const FilterPillComponent: React.FC<FilterPillProps> = ({
  filter,
  path,
  onFilterChange,
  shiftOptions,
  personOptions,
  tenureOptions,
  onRemove,
}) => {
  const [opened, setOpened] = React.useState(false);
  const keyBase = path.length > 0 ? path.join("-") : "root";

  const handleSubmit = (updated: AppointmentFilter) => {
    onFilterChange(path, updated);
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={320}
      position="bottom-start"
      withinPortal
      shadow="md"
      trapFocus
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Popover.Target>
        <Pill
          key={`pill-${keyBase}`}
          component="button"
          onClick={() => setOpened((o) => !o)}
          style={{ cursor: "pointer" }}
          styles={{ root: { borderRadius: "var(--mantine-radius-sm)" } }}
          radius="sm"
          withRemoveButton
          onRemove={() => onRemove(path)}
          removeButtonProps={{
            color: "red",
            variant: "subtle",
            size: "xs",
            onClick: (event) => event.stopPropagation(),
          }}
        >
          {describeFilter(filter)}
        </Pill>
      </Popover.Target>
      <Popover.Dropdown>
        <FilterEditor
          filter={filter}
          shiftOptions={shiftOptions}
          personOptions={personOptions}
          tenureOptions={tenureOptions}
          onSubmit={handleSubmit}
          onCancel={() => setOpened(false)}
        />
      </Popover.Dropdown>
    </Popover>
  );
};

interface FilterEditorProps {
  filter: AppointmentFilter;
  shiftOptions: ShiftOption[];
  personOptions: PersonOption[];
  tenureOptions: { value: string; label: string }[];
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}

const FilterEditor: React.FC<FilterEditorProps> = ({ filter, shiftOptions, personOptions, tenureOptions, onSubmit, onCancel }) => {
  switch (filter.kind) {
    case "dateRange":
      return <DateRangeFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    case "dayOfWeek":
      return <DayOfWeekFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    case "role":
      return <RoleFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    case "position":
      return <PositionFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    case "person":
      return (
        <PersonFilterEditor
          filter={filter}
          personOptions={personOptions}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case "shift":
      return (
        <ShiftFilterEditor
          filter={filter}
          shiftOptions={shiftOptions}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case "tenure":
      return <TenureFilterEditor filter={filter} tenureOptions={tenureOptions} onSubmit={onSubmit} onCancel={onCancel} />;
    case "appointmentType":
      return <AppointmentTypeFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    case "stillAppointed":
      return <StillAppointedFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    case "appointmentStatus":
      return <AppointmentStatusFilterEditor filter={filter} onSubmit={onSubmit} onCancel={onCancel} />;
    default:
      return null;
  }
};

const DateRangeFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "dateRange" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const [value, setValue] = React.useState<DatesRangeValue<Date>>([
    filter.from ? DateTime.fromISO(filter.from).toJSDate() : null,
    filter.to ? DateTime.fromISO(filter.to).toJSDate() : null,
  ]);

  const normalizeDateInput = (input: Date | string | null | undefined) => {
    if (!input) return undefined;
    if (input instanceof Date) {
      const dt = DateTime.fromJSDate(input, { zone: "utc" }).startOf("day");
      return dt.isValid ? dt.toISODate() : undefined;
    }
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (trimmed.length === 0) return undefined;
      const dt = DateTime.fromISO(trimmed, { zone: "utc" }).startOf("day");
      return dt.isValid ? dt.toISODate() : undefined;
    }
    return undefined;
  };

  const handleSave = () => {
    const [start, end] = value;
    onSubmit({
      kind: "dateRange",
      from: normalizeDateInput(start),
      to: normalizeDateInput(end),
    });
  };

  return (
    <Stack gap="sm">
      <Group gap={6} justify="flex-start" wrap="wrap">
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            const end = DateTime.now().setZone("utc").startOf("day");
            const start = end.minus({ days: 6 });
            setValue([start.toJSDate(), end.toJSDate()]);
          }}
        >
          Last 7 days
        </Button>
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            const end = DateTime.now().setZone("utc").startOf("day");
            const start = end.startOf("year");
            setValue([start.toJSDate(), end.toJSDate()]);
          }}
        >
          This year
        </Button>
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            const today = DateTime.now().setZone("utc").startOf("day");
            const start = today.startOf("month");
            const end = today.endOf("month").startOf("day");
            setValue([start.toJSDate(), end.toJSDate()]);
          }}
        >
          This month
        </Button>
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            const today = DateTime.now().setZone("utc").startOf("day").minus({ months: 1 });
            const start = today.startOf("month");
            const end = today.endOf("month").startOf("day");
            setValue([start.toJSDate(), end.toJSDate()]);
          }}
        >
          Last month
        </Button>
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            const startOfLastYear = DateTime.now().setZone("utc").startOf("year").minus({ years: 1 });
            setValue([startOfLastYear.toJSDate(), startOfLastYear.endOf("year").toJSDate()]);
          }}
        >
          Last year
        </Button>
      </Group>
      <DatePickerInput
        type="range"
        label="Date range"
        value={value}
        onChange={(range) => setValue(range as [Date | null, Date | null])}
        allowSingleDateInRange
        clearable
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </Group>
    </Stack>
  );
};

const DayOfWeekFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "dayOfWeek" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const [value, setValue] = React.useState<string[]>(filter.days.map((d) => d.toString()));

  const handleSave = () => {
    onSubmit({
      kind: "dayOfWeek",
      days: value.map((v) => Number(v)).filter((v) => !Number.isNaN(v)),
    });
  };

  return (
    <Stack gap="sm">
      <MultiSelect
        label="Days of the week"
        data={DAY_OF_WEEK_OPTIONS}
        value={value}
        onChange={setValue}
        searchable
        nothingFoundMessage="No matches"
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={value.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const RoleFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "role" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const initialRole =
    typeof filter.role === "number"
      ? ((Role as unknown as Record<number, string>)[filter.role] ?? filter.role.toString())
      : filter.role.toString();
  const [role, setRole] = React.useState(initialRole);
  const initialGrades = React.useMemo(() => {
    if (Array.isArray(filter.grades) && filter.grades.length > 0) {
      return filter.grades.map((value) => `${value}`);
    }
    const legacy = (filter as unknown as { grade?: string | number | null | undefined }).grade;
    if (legacy != null) {
      const text = `${legacy}`.trim();
      return text.length > 0 ? [text] : [];
    }
    return [];
  }, [filter]);
  const [grades, setGrades] = React.useState<string[]>(initialGrades);
  const roleChangeRef = React.useRef(initialRole);

  const roleOptions = React.useMemo(() => {
    if (ROLE_OPTIONS.some((opt) => opt.value === initialRole)) {
      return ROLE_OPTIONS;
    }
    return ROLE_OPTIONS.concat({
      value: initialRole,
      label: initialRole.charAt(0).toUpperCase() + initialRole.slice(1),
    });
  }, [initialRole]);

  React.useEffect(() => {
    if (roleChangeRef.current !== role) {
      roleChangeRef.current = role;
      setGrades([]);
    }
  }, [role]);

  const gradeOptions = React.useMemo(() => {
    const options = getGradeOptionsForRole(role, grades);
    if (!options) {
      return grades.map((value) => ({ value, label: value }));
    }
    const optionValues = new Set(options.map((opt) => opt.value));
    const extras = grades.filter((value) => !optionValues.has(value)).map((value) => ({ value, label: value }));
    return [...options, ...extras];
  }, [role, grades]);

  const handleSave = () => {
    const normalizedGrades = Array.from(
      new Set(
        grades
          .map((value) => value.trim())
          .filter((value) => value.length > 0)
          .map((value) => {
            const numeric = Number(value);
            return Number.isNaN(numeric) ? value : numeric;
          }),
      ),
    );

    const payload: Extract<AppointmentFilter, { kind: "role" }> = {
      kind: "role",
      role,
    };

    if (normalizedGrades.length > 0) {
      payload.grades = normalizedGrades;
    }

    onSubmit(payload);
  };

  return (
    <Stack gap="sm">
      <Select
        label="Role"
        data={roleOptions}
        value={role}
        onChange={(value) => value && setRole(value)}
        searchable
      />
      <TagsInput
        label="Grades"
        data={gradeOptions}
        value={grades}
        onChange={setGrades}
        clearable
        placeholder="All grades"
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </Group>
    </Stack>
  );
};

const PositionFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "position" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const [data, setData] = React.useState(() =>
    Array.from(new Set(filter.values)).map((value) => ({ value, label: value })),
  );
  const [values, setValues] = React.useState<string[]>(filter.values);

  const handleValueChange = (nextValues: string[]) => {
    setValues(nextValues);
    setData((current) => {
      const existing = new Set(current.map((item) => item.value));
      const additions = nextValues
        .filter((val) => !existing.has(val))
        .map((val) => ({ value: val, label: val }));
      return additions.length > 0 ? [...current, ...additions] : current;
    });
  };

  const handleSave = () => {
    onSubmit({
      kind: "position",
      values,
    });
  };

  return (
    <Stack gap="sm">
      <TagsInput
        label="Positions"
        data={data}
        value={values}
        onChange={handleValueChange}
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={values.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const PersonFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "person" }>;
  personOptions: PersonOption[];
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, personOptions, onSubmit, onCancel }) => {
  const mergePersonOptions = React.useCallback(
    () => {
      const knownIds = new Set(personOptions.map((opt) => opt.value));
      const extras = filter.ids
        .filter((id) => !knownIds.has(id))
        .map((id) => ({ value: id, label: id }));
      return [...personOptions, ...extras];
    },
    [personOptions, filter.ids],
  );

  const [data, setData] = React.useState<PersonOption[]>(() => mergePersonOptions());
  const [value, setValue] = React.useState<string[]>(filter.ids);

  React.useEffect(() => {
    setData(mergePersonOptions());
  }, [mergePersonOptions]);

  const handleSave = () => {
    onSubmit({
      kind: "person",
      ids: value.filter((id) => id && id.trim().length > 0),
    });
  };

  return (
    <Stack gap="sm">
      <TagsInput
        label="Persons"
        data={data}
        value={value}
        onChange={(next) => {
          setValue(next);
          setData((current) => {
            const known = new Set(current.map((item) => item.value));
            const additions = next
              .filter((id) => id && !known.has(id))
              .map((id) => ({ value: id, label: id }));
            return additions.length ? [...current, ...additions] : current;
          });
        }}
        clearable
        placeholder="All persons"
      />
      <Text size="xs" c="dimmed" ta="left" style={{ lineHeight: 1.4 }}>
        Filter to active association members. You can also paste specific IDs if needed.
      </Text>
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={value.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const ShiftFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "shift" }>;
  shiftOptions: ShiftOption[];
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, shiftOptions, onSubmit, onCancel }) => {
  const mergeShiftOptions = React.useCallback(
    () => {
      const knownIds = new Set(shiftOptions.map((opt) => opt.value));
      const extras = filter.ids
        .filter((id) => !knownIds.has(id))
        .map((id) => ({ value: id, label: id }));
      return [...shiftOptions, ...extras];
    },
    [shiftOptions, filter.ids],
  );

  const [data, setData] = React.useState<ShiftOption[]>(() => mergeShiftOptions());
  const [value, setValue] = React.useState<string[]>(filter.ids);

  React.useEffect(() => {
    setData(mergeShiftOptions());
  }, [mergeShiftOptions]);

  const handleValueChange = (nextValues: string[]) => {
    setValue(nextValues);
    setData((current) => {
      const known = new Set(current.map((item) => item.value));
      const additions = nextValues
        .filter((id) => id && !known.has(id))
        .map((id) => ({ value: id, label: id }));
      return additions.length ? [...current, ...additions] : current;
    });
  };

  const handleSave = () => {
    onSubmit({
      kind: "shift",
      ids: value,
    });
  };

  return (
    <Stack gap="sm">
      <TagsInput
        label="Shifts"
        data={data}
        value={value}
        onChange={handleValueChange}
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={value.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const TenureFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "tenure" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
  tenureOptions: { value: string; label: string }[];
}> = ({ filter, onSubmit, onCancel, tenureOptions }) => {
  const [value, setValue] = React.useState<string[]>(filter.values);

  const handleSave = () => {
    onSubmit({
      kind: "tenure",
      values: value,
    });
  };

  return (
    <Stack gap="sm">
      <MultiSelect
        label="Tenure"
        data={tenureOptions}
        value={value}
        onChange={setValue}
        searchable
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={value.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const AppointmentTypeFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "appointmentType" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const [value, setValue] = React.useState<string[]>(filter.types);

  const handleSave = () => {
    onSubmit({
      kind: "appointmentType",
      types: value,
    });
  };

  return (
    <Stack gap="sm">
      <MultiSelect
        label="Appointment Types"
        data={APPOINTMENT_TYPE_OPTIONS}
        value={value}
        onChange={setValue}
        searchable
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={value.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const AppointmentStatusFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "appointmentStatus" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const [value, setValue] = React.useState<string[]>(
    filter.statuses.map((status) => canonicaliseStatusValue(status)),
  );

  const handleSave = () => {
    onSubmit({
      kind: "appointmentStatus",
      statuses: value,
    });
  };

  return (
    <Stack gap="sm" style={{ width: 320 }}>
      <MultiSelect
        label="Appointment statuses"
        data={APPOINTMENT_STATUS_OPTIONS}
        value={value}
        onChange={setValue}
        searchable
        nothingFoundMessage="No status matches"
      />
      <Text size="xs" c="dimmed" ta="left" style={{ lineHeight: 1.4 }}>
        Removed or Rejected statuses typically only apply when Still appointed is set to No or Either.
      </Text>
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={value.length === 0}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

const StillAppointedFilterEditor: React.FC<{
  filter: Extract<AppointmentFilter, { kind: "stillAppointed" }>;
  onSubmit: (filter: AppointmentFilter) => void;
  onCancel: () => void;
}> = ({ filter, onSubmit, onCancel }) => {
  const [value, setValue] = React.useState<boolean>(filter.value ?? true);

  const handleSave = () => {
    onSubmit({
      kind: "stillAppointed",
      value,
    });
  };

  return (
    <Stack gap="sm">
      <Select
        label="Still appointed"
        data={[
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ]}
        value={value ? "true" : "false"}
        onChange={(val) => setValue(val === "true")}
      />
      <Text size="xs" c="dimmed" ta="left" style={{ lineHeight: 1.4 }}>
        Setting this to No captures officials who were removed from games. Filtering by the removed statuses is often
        more useful when you need to focus on those appointments.
      </Text>
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </Group>
    </Stack>
  );
};

function updateExpressionNode(
  node: AppointmentQueryNode,
  path: ExpressionPath,
  updater: (node: AppointmentQueryNode) => AppointmentQueryNode,
): AppointmentQueryNode {
  if (path.length === 0) {
    return updater(node);
  }

  if (node.type === "filter") {
    return updater(node);
  }

  const [targetIndex, ...rest] = path;
  const updatedNodes = node.nodes.map((child, index) =>
    index === targetIndex ? updateExpressionNode(child, rest, updater) : child,
  );

  return {
    type: node.type,
    nodes: updatedNodes,
  };
}

function removeExpressionNode(node: AppointmentQueryNode, path: ExpressionPath): AppointmentQueryNode | null {
  if (path.length === 0) {
    return null;
  }

  if (node.type === "filter") {
    return null;
  }

  const [targetIndex, ...rest] = path;

  const updatedChildren: AppointmentQueryNode[] = [];

  node.nodes.forEach((child, index) => {
    if (index !== targetIndex) {
      updatedChildren.push(child);
      return;
    }

    if (rest.length === 0) {
      // remove this child entirely
      return;
    }

    const result = removeExpressionNode(child, rest);
    if (result) {
      updatedChildren.push(result);
    }
  });

  if (updatedChildren.length === 0) {
    return null;
  }

  if (updatedChildren.length === 1) {
    return updatedChildren[0];
  }

  return {
    type: node.type,
    nodes: updatedChildren,
  };
}

function toIsoDate(value: Date | string | null | undefined): string | undefined {
  if (!value) return undefined;

  let dateValue: Date | null = null;
  if (value instanceof Date) {
    dateValue = value;
  } else if (typeof value === "string") {
    const parsed = new Date(value);
    dateValue = Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    return undefined;
  }

  const dt = DateTime.fromJSDate(dateValue, { zone: "utc" });
  if (!dt.isValid) {
    return undefined;
  }

  return dt.startOf("day").toISODate() ?? undefined;
}

function formatBasicDateLabel(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  let dt: DateTime | null = null;
  if (value instanceof Date) {
    dt = DateTime.fromJSDate(value, { zone: "utc" });
  } else if (typeof value === "string") {
    dt = DateTime.fromISO(value, { zone: "utc" });
  }
  if (!dt || !dt.isValid) {
    return null;
  }
  return dt.toFormat("dd LLL yyyy");
}

function buildBasicExpression(filters: BasicFilters): {
  expression: AppointmentQueryNode | null;
  error: string | null;
} {
  const [startDate, endDate] = filters.dateRange;
  const from = toIsoDate(startDate);
  const to = toIsoDate(endDate);

  if (!from && !to) {
    return { expression: null, error: "Select at least a start or end date." };
  }

  if (from && to) {
    const fromDate = DateTime.fromISO(from);
    const toDate = DateTime.fromISO(to);
    if (fromDate > toDate) {
      return { expression: null, error: "Start date must be before end date." };
    }
  }

  const nodes: AppointmentQueryNode[] = [];

  nodes.push({
    type: "filter",
    filter: {
      kind: "dateRange",
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  });

  if (filters.stillAppointed !== null) {
    nodes.push({
      type: "filter",
      filter: {
        kind: "stillAppointed",
        value: filters.stillAppointed,
      },
    });
  }

  if (filters.statuses.length > 0) {
    const statuses = Array.from(new Set(filters.statuses.map((status) => canonicaliseStatusValue(status))));
    if (statuses.length > 0) {
      nodes.push({
        type: "filter",
        filter: {
          kind: "appointmentStatus",
          statuses,
        },
      });
    }
  }

  if (filters.appointmentTypes.length > 0) {
    const types = Array.from(new Set(filters.appointmentTypes.map((type) => type.toLowerCase())));
    if (types.length > 0) {
      nodes.push({
        type: "filter",
        filter: {
          kind: "appointmentType",
          types,
        },
      });
    }
  }

  if (filters.dayOfWeek.length > 0) {
    const days = Array.from(
      new Set(
        filters.dayOfWeek
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value >= 1 && value <= 7),
      ),
    );
    if (days.length > 0) {
      nodes.push({
        type: "filter",
        filter: {
          kind: "dayOfWeek",
          days,
        },
      });
    }
  }

  if (filters.tenure.length > 0) {
    const values = Array.from(new Set(filters.tenure.map((value) => value.toLowerCase())));
    if (values.length > 0) {
      nodes.push({
        type: "filter",
        filter: {
          kind: "tenure",
          values,
        },
      });
    }
  }

  if (filters.role) {
    const grades = Array.from(
      new Set(
        filters.roleGrades
          .map((grade) => grade.trim())
          .filter((grade) => grade.length > 0)
          .map((grade) => {
            const numeric = Number(grade);
            return Number.isNaN(numeric) ? grade : numeric;
          }),
      ),
    );

    const roleFilter: Extract<AppointmentFilter, { kind: "role" }> = {
      kind: "role",
      role: filters.role,
    };

    if (grades.length > 0) {
      roleFilter.grades = grades;
    }

    nodes.push({
      type: "filter",
      filter: roleFilter,
    });
  }

  if (filters.persons.length > 0) {
    const ids = Array.from(new Set(filters.persons.map((id) => id.trim()).filter((id) => id.length > 0)));
    if (ids.length > 0) {
      nodes.push({
        type: "filter",
        filter: {
          kind: "person",
          ids,
        },
      });
    }
  }

  if (filters.shifts.length > 0) {
    const ids = Array.from(new Set(filters.shifts.filter((id) => id && id.trim().length > 0))).map((id) => id.trim());
    if (ids.length > 0) {
      nodes.push({
        type: "filter",
        filter: {
          kind: "shift",
          ids,
        },
      });
    }
  }

  return {
    expression: { type: "and", nodes },
    error: null,
  };
}

function stringifyAppointmentQuery(node: AppointmentQueryNode, parentType: "and" | "or" | null = null): string {
  if (node.type === "filter") {
    return stringifyFilter(node.filter);
  }

  const joiner = ` ${node.type.toUpperCase()} `;
  const inner = node.nodes.map((child) => stringifyAppointmentQuery(child, node.type)).join(joiner);
  const needsParentheses = parentType !== null && parentType !== node.type;
  return needsParentheses ? `(${inner})` : inner;
}

function stringifyFilter(filter: AppointmentFilter): string {
  switch (filter.kind) {
    case "dateRange": {
      const args: string[] = [];
      if (filter.from) args.push(quoteString(filter.from));
      if (filter.to) {
        if (args.length === 0) {
          args.push(quoteString(filter.to));
        } else {
          args.push(quoteString(filter.to));
        }
      }
      return `DateRange(${args.join(",")})`;
    }
    case "dayOfWeek":
      return `Day(${filter.days.join(",")})`;
    case "role": {
      const args = [quoteString(filter.role.toString())];
      const grades = resolveRoleFilterGrades(filter);
      grades.forEach((grade) => {
        const gradeText = `${grade}`.trim();
        if (gradeText.length === 0) return;
        args.push(Number.isNaN(Number(gradeText)) ? quoteString(gradeText) : gradeText);
      });
      return `RoleFilter(${args.join(",")})`;
    }
    case "position": {
      const args = filter.values.map((value) => quoteString(value));
      return `Position(${args.join(",")})`;
    }
    case "person": {
      const args = filter.ids.map((id) => quoteString(id));
      return `Person(${args.join(",")})`;
    }
    case "shift": {
      const args = filter.ids.map((id) => quoteString(id));
      return `Shift(${args.join(",")})`;
    }
    case "tenure": {
      const args = filter.values.map((value) => quoteString(value));
      return `Tenure(${args.join(",")})`;
    }
    case "appointmentType": {
      const args = filter.types.map((value) => quoteString(value));
      return `AppointmentType(${args.join(",")})`;
    }
    case "appointmentStatus": {
      const args = filter.statuses.map((value) => quoteString(canonicaliseStatusValue(value)));
      return `AppointmentStatus(${args.join(",")})`;
    }
    case "stillAppointed":
      return `StillAppointed(${filter.value ? "TRUE" : "FALSE"})`;
    default:
      return "";
  }
}

function quoteString(value: string): string {
  const escaped = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `"${escaped}"`;
}

function expressionContainsDateRange(node: AppointmentQueryNode): boolean {
  if (node.type === "filter") {
    return node.filter.kind === "dateRange";
  }
  return node.nodes.some((child) => expressionContainsDateRange(child));
}

function normalizeRoleKey(role: string | number): string {
  const raw =
    typeof role === "number"
      ? ((Role as unknown as Record<number, string>)[Number(role)] ?? role.toString())
      : role.toString();
  return raw.replace(/[^a-z]/gi, "").toLowerCase();
}

function getRoleDisplayLabel(role: string | number): string {
  const normalized = normalizeRoleKey(role);
  const option = ROLE_OPTIONS.find((opt) => normalizeRoleKey(opt.value) === normalized);
  if (option) return option.label;

  if (typeof role === "number") {
    const enumName = (Role as unknown as Record<number, string>)[role];
    if (enumName) {
      return enumName.charAt(0).toUpperCase() + enumName.slice(1);
    }
  }

  const raw = role.toString();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function getGradeOptionsForRole(role: string | number, currentGrades?: string[]): { value: string; label: string }[] | null {
  const key = normalizeRoleKey(role);
  let options: { value: string; label: string }[] | null = null;

  switch (key) {
    case "referee":
      options = BasketballGrade.all().map((gr) => ({
        value: gr.toString(),
        label: BasketballGrade.toString(gr),
      }));
      break;
    case "scoretable":
      options = SCORER_GRADE_VALUES.map((gr) => ({
        value: gr.toString(),
        label: ScorerGrade.toShortString(gr),
      }));
      break;
    case "statistician":
      options = StatisticianGrade.all().map((gr) => ({
        value: gr.toString(),
        label: StatisticianGrade.toShortString(gr),
      }));
      break;
    case "refereecoach":
      options = RefereeCoachGrade.all().map((gr) => ({
        value: gr.toString(),
        label: RefereeCoachGrade.toShortString(gr),
      }));
      break;
    default:
      options = null;
  }

  if (options && currentGrades && currentGrades.length > 0) {
    const optionValues = new Set(options.map((opt) => opt.value));
    const extras = currentGrades
      .map((grade) => grade.trim())
      .filter((grade) => grade.length > 0 && !optionValues.has(grade))
      .map((grade) => ({ value: grade, label: grade }));
    if (extras.length > 0) {
      options = options.concat(extras);
    }
  }

  return options;
}

function getRoleGradeLabels(role: string | number, grades?: (number | string)[] | null): string[] {
  if (!grades || grades.length === 0) {
    return [];
  }

  const values = grades
    .map((grade) => `${grade}`.trim())
    .filter((grade) => grade.length > 0);

  if (values.length === 0) {
    return [];
  }

  const options = getGradeOptionsForRole(role, values);
  if (!options) {
    return values;
  }

  const optionMap = new Map(options.map((opt) => [opt.value, opt.label]));
  return values.map((value) => optionMap.get(value) ?? value);
}

function getAppointmentTypeLabel(type: string) {
  return APPOINTMENT_TYPE_LABEL_MAP[type.toLowerCase()] || type;
}

function getAppointmentStatusDisplayLabel(status: AppointmentStatus): string {
  const label = AppointmentStatus.toString(status);
  if (label && !label.toLowerCase().startsWith("unknown")) {
    return label;
  }
  const key = AppointmentStatus[status] as string | undefined;
  if (key) {
    return key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/(^|\s)([a-z])/g, (_match, prefix: string, char: string) => `${prefix}${char.toUpperCase()}`);
  }
  return `Status ${status}`;
}

function canonicaliseStatusValue(value: string): string {
  if (!value) return value;
  const trimmed = value.trim();
  if (trimmed.length === 0) return trimmed;
  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric)) {
    const key = AppointmentStatus[numeric as AppointmentStatus];
    return typeof key === "string" ? key : trimmed;
  }
  const matchKey = Object.keys(AppointmentStatus)
    .filter((key) => isNaN(Number(key)))
    .find((key) => key.toLowerCase() === trimmed.toLowerCase());
  return matchKey ?? trimmed;
}

function formatAppointmentStatusValue(value: string): string {
  if (!value) return value;
  const direct = APPOINTMENT_STATUS_LABEL_MAP[value];
  if (direct) return direct;
  const lower = value.toLowerCase();
  if (APPOINTMENT_STATUS_LABEL_MAP[lower]) {
    return APPOINTMENT_STATUS_LABEL_MAP[lower];
  }

  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    const numericKey = String(numeric);
    if (APPOINTMENT_STATUS_LABEL_MAP[numericKey]) {
      return APPOINTMENT_STATUS_LABEL_MAP[numericKey];
    }
    const statusEnum = AppointmentStatus[numeric as AppointmentStatus];
    if (typeof statusEnum === "string") {
      return getAppointmentStatusDisplayLabel(numeric as AppointmentStatus);
    }
  }

  const matchKey = Object.keys(AppointmentStatus)
    .filter((key) => isNaN(Number(key)))
    .find((key) => key.toLowerCase() === lower);
  if (matchKey) {
    return getAppointmentStatusDisplayLabel(
      AppointmentStatus[matchKey as keyof typeof AppointmentStatus] as AppointmentStatus,
    );
  }

  return value;
}

interface MultiSelectChipProps {
  label: string;
  summary: string;
  value: string[];
  onChange: (value: string[]) => void;
  data: { value: string; label: string }[];
  withinPortal?: boolean;
  dropdownLabel?: string;
  emptyLabel?: string;
  dropdownWidth?: number;
  helperText?: string;
}

const MultiSelectChip: React.FC<MultiSelectChipProps> = ({
  label,
  summary,
  value,
  onChange,
  data,
  withinPortal = true,
  dropdownLabel,
  emptyLabel = "Nothing found",
  dropdownWidth,
  helperText,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => setSearch(""),
  });
  const [search, setSearch] = React.useState("");
  const active = value.length > 0;
  const searchLower = search.trim().toLowerCase();
  const filtered = data.filter((item) => item.label.toLowerCase().includes(searchLower));

  const toggleValue = (itemValue: string) => {
    if (value.includes(itemValue)) {
      onChange(value.filter((v) => v !== itemValue));
    } else {
      onChange([...value, itemValue]);
    }
  };

  const dropdownStyle = dropdownWidth
    ? { width: dropdownWidth, minWidth: dropdownWidth }
    : { minWidth: 220 };

  return (
    <Combobox store={combobox} withinPortal={withinPortal} position="bottom-start" shadow="md">
      <Combobox.Target>
        <Button
          variant={active ? "filled" : "light"}
          radius="sm"
          size="sm"
          onClick={() => combobox.toggleDropdown()}
          rightSection={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
          styles={(theme) => ({
            root: {
              height: "auto",
              padding: `${theme.spacing.xs * 0.75}px ${theme.spacing.sm}px`,
              borderRadius: theme.radius.sm,
            },
            inner: { alignItems: "center", textAlign: "left", gap: theme.spacing.xs },
            section: { marginTop: 0 },
          })}
        >
          <Stack gap={2} style={{ textAlign: "left" }}>
            <Text size="xs" fw={600}>
              {label}
            </Text>
            <Text size="xs" c="dimmed">
              {summary}
            </Text>
          </Stack>
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown style={dropdownStyle}>
        <Stack gap="xs">
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder={`Search ${label.toLowerCase()}`}
          />
          {helperText && (
            <Text size="xs" c="dimmed" ta="left" style={{ lineHeight: 1.4 }}>
              {helperText}
            </Text>
          )}
          <ScrollArea.Autosize mah={200} type="auto">
            <Combobox.Options>
              {filtered.length === 0 ? (
                <Combobox.Empty>{emptyLabel}</Combobox.Empty>
              ) : (
                filtered.map((item) => {
                  const checked = value.includes(item.value);
                  return (
                    <Combobox.Option value={item.value} key={item.value} onClick={() => toggleValue(item.value)}>
                      <Group gap="sm" wrap="nowrap">
                        <Checkbox
                          checked={checked}
                          onChange={() => { }}
                          tabIndex={-1}
                          style={{ pointerEvents: "none" }}
                        />
                        <Text size="sm">{item.label}</Text>
                      </Group>
                    </Combobox.Option>
                  );
                })
              )}
            </Combobox.Options>
          </ScrollArea.Autosize>
        </Stack>
      </Combobox.Dropdown>
    </Combobox>
  );
};

function describeFilter(filter: AppointmentFilter): string {
  switch (filter.kind) {
    case "dateRange":
      return `Date ${filter.from || "…"} → ${filter.to || "…"}`;
    case "dayOfWeek":
      return `Days ${filter.days.map((d) => DAY_NAMES[(d - 1 + 7) % 7]).join(", ")}`;
    case "role": {
      const roleLabel = getRoleDisplayLabel(filter.role);
      const gradeLabels = getRoleGradeLabels(filter.role, resolveRoleFilterGrades(filter));
      return `Role ${roleLabel}${gradeLabels.length > 0 ? ` (${gradeLabels.join(", ")})` : ""}`;
    }
    case "position":
      return `Position ${filter.values.join(", ")}`;
    case "person":
      return `Person ${filter.ids.length} selected`;
    case "shift":
      return `Shift ${filter.ids.length} selected`;
    case "tenure":
      return `Tenure ${filter.values.join(", ")}`;
    case "appointmentType":
      return `Type ${filter.types.map((t) => getAppointmentTypeLabel(t)).join(", ")}`;
    case "appointmentStatus":
      return `Status ${filter.statuses.map((status) => formatAppointmentStatusValue(status)).join(", ")}`;
    case "stillAppointed":
      return filter.value ? "Still appointed" : "Not still appointed";
    default:
      return "Filter";
  }
}

function resolveRoleFilterGrades(filter: Extract<AppointmentFilter, { kind: "role" }>): (number | string)[] {
  if (Array.isArray(filter.grades) && filter.grades.length > 0) {
    return filter.grades;
  }
  const legacy = (filter as unknown as { grade?: number | string | null | undefined }).grade;
  if (legacy == null) {
    return [];
  }
  const text = `${legacy}`.trim();
  if (text.length === 0) {
    return [];
  }
  return [legacy];
}

function formatTenure(value?: string | null, getTenureLabel?: (id: string | null) => string) {
  if (!value) return "-";
  if (getTenureLabel) return getTenureLabel(value);
  // Fallback to capitalize
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatGrade(role?: Role, grade?: number | string | null) {
  if (grade == null || role == null) return "-";
  const numericGrade = typeof grade === "number" ? grade : Number(grade);
  if (Number.isNaN(numericGrade)) {
    return grade?.toString() || "-";
  }
  switch (role) {
    case Role.referee:
      return BasketballGrade.toString(numericGrade as BasketballGrade);
    case Role.scoretable:
      return ScorerGrade.toShortString(numericGrade as ScorerGrade);
    case Role.statistician:
      return StatisticianGrade.toShortString(numericGrade as StatisticianGrade);
    case Role.refereeCoach:
      return RefereeCoachGrade.toShortString(numericGrade as RefereeCoachGrade);
    default:
      return `${numericGrade}`;
  }
}

/**
 * Extract person-related filters from an appointment query expression.
 * Returns filters that can be used to count people in the database.
 */
function extractPersonFilters(node: AppointmentQueryNode | null): {
  tenures: string[];
  role: string | null;
  roleGrades: (number | string)[];
  personIds: string[];
} {
  const result = {
    tenures: [] as string[],
    role: null as string | null,
    roleGrades: [] as (number | string)[],
    personIds: [] as string[],
  };

  if (!node) return result;

  const collectFilters = (n: AppointmentQueryNode) => {
    if (n.type === "filter") {
      const filter = n.filter;
      switch (filter.kind) {
        case "tenure":
          result.tenures.push(...filter.values);
          break;
        case "role":
          result.role = filter.role;
          if (filter.grades) {
            result.roleGrades.push(...filter.grades);
          }
          break;
        case "person":
          result.personIds.push(...filter.ids);
          break;
      }
    } else if (n.type === "and" || n.type === "or") {
      n.nodes.forEach(collectFilters);
    }
  };

  collectFilters(node);
  return result;
}

export default AppointmentReportingPanel;

